import React, { useState, useRef, useEffect } from 'react'
import { ArrowRightIcon, AttachmentIcon } from '@shopify/polaris-icons'
import { IconSource } from '@shopify/polaris'
import { Icon } from '@shopify/polaris'
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage'
import { v4 as uuidv4 } from 'uuid'
import { getStorageBucketRef } from '../../services/firebase/firebaseConfig'

interface TextInputProps {
    onCommand: (command: string, attachmentObjects?: any) => void
    autoFocus?: boolean
    initialSuggestion?: string
}

export interface Attachment {
    url: string
    type: string
    filename: string
    file?: File  // Add the actual File object
}

const expandSuggestion = (suggestion: string): string => {
    // Extract the action from the suggestion (usually the first word)
    const action = suggestion.split(' ')[0].toLowerCase()

    switch (action) {
        case 'create':
            if (suggestion.includes('order')) {
                return `create a new order with these details :  
- user: "akram.m@shyftbase.com"
- customer name: "John Doe"
- vendor: "Best Buy ***Best Buy Corp" 
- region: "Canada***Brampton" 
- Invoice: "417238942"
- selected date : Aug 7, 2025`
            } else if (suggestion.includes('BOL')) {
                return `Create BOL with the following information:
- Invoice: BOL98765
- Vendor: XYZ Shipping
- Distribution Zone: East
- Order Type: BOL
- Order number: 87654321
- Shipping details: Express Shipping
- Product list: Item X, Item Y, Item Z
- Special handling instructions: Fragile items`
            } else if (suggestion.toLowerCase().includes('return to store')) {
                return `Create return to store with the following details:
- Invoice: RET54321
- Vendor: RetailCo
- Distribution Zone: South
- Order Type: Return
- Order number: 54321
- Customer ID: 67890
- Products to return: Item A, Item B
- Return reason: Damaged on arrival
- Store location: Store #123
- Preferred return date: 2023-06-15`
            } else if (suggestion.toLowerCase().includes('pickup from store')) {
                return `Create pickup from store with the following details:
- Invoice: PIC98765
- Vendor: PickupMart
- Distribution Zone: West
- Order Type: Pickup
- Order number: 98765
- Customer ID: 45678
- Products: Widget X, Widget Y
- Store location: Store #456
- Preferred pickup date and time: 2023-06-20 14:00
- Contact information: john.doe@example.com, 555-123-4567`
            }
            break

        case 'cancel':
            return `Cancel order with the following details:
- Invoice: CAN76543
- Vendor: CancelCorp
- Distribution Zone: Central
- Order Type: Cancellation
- Order number: 76543
- Cancellation reason: Customer changed mind
- Customer ID: 34567
- Refund details (if applicable): Full refund to original payment method`

        case 'schedule':
        case 'reschedule':
            return `${action.charAt(0).toUpperCase() + action.slice(1)} order with these specifications:
- Invoice: SCH23456
- Vendor: ScheduleCo
- Distribution Zone: Northeast
- Order Type: Scheduling
- Order number: 23456
- Preferred date and time: 2023-06-25 10:00
- Contact information: jane.smith@example.com, 555-987-6543
- Product list: Service A, Service B`

        case 'geocode':
            return `Geocode the following orders (invoices, date ranges, etc)`

        case 'print':
            return `Print report with the following criteria:
- Invoice: PRT45678
- Vendor: PrintCo
- Distribution Zone: Central
- Order Type: Report
- Date range (start and end date): 2023-05-01 to 2023-05-31
- Distribution zones: All zones
- Client selection: All clients
- Report type: Monthly summary
- Sort order: By date, descending`

        default:
            return suggestion
    }

    return suggestion
}

const TextInput = ({ onCommand, autoFocus, initialSuggestion }: TextInputProps) => {
    const [input, setInput] = useState('')
    const [attachments, setAttachments] = useState<Attachment[]>([])
    const [isUploading, setIsUploading] = useState(false)
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const formData = new FormData()

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus()
        }
    }, [autoFocus])

    // Initialize input state when component mounts
    useEffect(() => {
        if (initialSuggestion && initialSuggestion.trim() !== '') {
            const expandedSuggestion = expandSuggestion(initialSuggestion)
            setInput(expandedSuggestion)
        }
    }, []) // Empty dependency array means this runs only once on mount

    useEffect(() => {
        if (initialSuggestion && initialSuggestion.trim() !== '') {
            const expandedSuggestion = expandSuggestion(initialSuggestion)
            setInput(expandedSuggestion)
            // Focus and move cursor to end of text
            if (inputRef.current) {
                inputRef.current.focus()
                inputRef.current.setSelectionRange(expandedSuggestion.length, expandedSuggestion.length)
            }
        } else if (initialSuggestion === '' || !initialSuggestion) {
            // Clear the input when initialSuggestion is explicitly set to empty string or undefined
            setInput('')
        }
    }, [initialSuggestion])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (input.trim()) {

            // Add attachment information to the command if there are attachments
            const commandWithAttachments = input.trim()
            // if (attachments.length > 0) {
            //     const attachmentUrls = attachments.map((a) => a.url).join(', ')
            //     commandWithAttachments += `\n\nAttachments: ${attachmentUrls}`
            // }

            // Pass both the formatted command text and the attachment objects
            onCommand(commandWithAttachments, attachments)
            setInput('')
            setAttachments([])
        } else {
            console.log('TextInput: Empty input, not submitting')
        }
    }

    const handleAttachmentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files?.length) return

        setIsUploading(true)
        const fileArray = Array.from(event.target.files)
        const storage = getStorageBucketRef('attachments')

        const promises = fileArray.map((file: File) => {
            const fileType = file.name.split('.').pop() || ''
            const storageRef = ref(storage, `/ai-attachments/${uuidv4()}.${fileType}`)
            const uploadTask = uploadBytesResumable(storageRef, file)

            return new Promise<Attachment>((resolve, reject) => {
                uploadTask.on(
                    'state_changed',
                    (snapshot) => {
                        // Progress tracking if needed
                    },
                    (err) => reject(err),
                    () => {
                        getDownloadURL(uploadTask.snapshot.ref).then((url) => {
                            const fileName = file.name
                            resolve({ url, type: fileType, filename: fileName, file: file })
                        })
                    }
                )
            })
        })

        try {
            const results = await Promise.all(promises)
            setAttachments((prev) => [...prev, ...results])
        } catch (err) {
            console.error('Error uploading attachments:', err)
        } finally {
            setIsUploading(false)
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const removeAttachment = (index: number) => {
        const attachment = attachments[index]
        if (!attachment) return

        // Extract the file path from the URL
        const filePath = attachment.url.split('?')[0].split('/o/')[1]
        if (!filePath) return

        // Decode the file path
        const decodedPath = decodeURIComponent(filePath)
        const storage = getStorageBucketRef('attachments')
        const deleteRef = ref(storage, decodedPath)

        // Remove from state first for better UX
        const newAttachments = attachments.filter((_, i) => i !== index)
        setAttachments(newAttachments)

        // Then delete from storage
        deleteObject(deleteRef)
            .then(() => console.log('Successfully removed attachment'))
            .catch((err) => console.error('Error removing attachment:', err))
    }

    return (
        <form onSubmit={handleSubmit} className="is-flex" style={{ gap: '8px', alignContent: "flex-end" }}>
            <div style={{ flex: 1, position: 'relative' }}>
                <textarea
                    ref={inputRef}
                    className="input is-small"
                    placeholder="Type your prompt and press Enter..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    style={{
                        minHeight: '80px',
                        resize: 'vertical',
                        padding: '8px',
                        lineHeight: '1.4',
                        width: '100%',
                        borderRadius: '4px',
                    }}
                    onKeyDown={(e) => {
                        console.log('TextInput: Key pressed:', e.key, 'Shift:', e.shiftKey)
                        if (e.key === 'Enter' && !e.shiftKey) {
                            console.log('TextInput: Enter key pressed without shift, submitting form')
                            e.preventDefault()
                            handleSubmit(e)
                        }
                    }}
                />

                {/* Attachments display area */}
                {attachments.length > 0 && (
                    <div className="box p-2 mt-2" style={{ backgroundColor: '#f5f5f5' }}>
                        <div className="is-flex is-align-items-center mb-2">
                            <span className="icon mr-2">
                                <i className="fas fa-paperclip"></i>
                            </span>
                            <span className="is-size-7 has-text-weight-bold">Attachments</span>
                        </div>
                        <div className="is-flex is-flex-wrap-wrap">
                            {attachments.map((attachment, index) => (
                                <div
                                    key={index}
                                    className="is-relative mr-2 mb-2"
                                    style={{ width: '64px', height: '64px' }}
                                >
                                    {attachment.type === 'pdf' ? (
                                        <div
                                            className="has-background-light is-flex is-align-items-center is-justify-content-center"
                                            style={{ width: '100%', height: '100%', borderRadius: '4px' }}
                                        >
                                            <span className="icon">
                                                <i className="fas fa-file-pdf"></i>
                                            </span>
                                        </div>
                                    ) : ['jpg', 'jpeg', 'png', 'gif'].includes(attachment.type.toLowerCase()) ? (
                                        <img
                                            src={attachment.url}
                                            alt={attachment.filename}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: '4px',
                                            }}
                                        />
                                    ) : (
                                        <div
                                            className="has-background-light is-flex is-align-items-center is-justify-content-center"
                                            style={{ width: '100%', height: '100%', borderRadius: '4px' }}
                                        >
                                            <span className="icon">
                                                <i className="fas fa-file"></i>
                                            </span>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="delete is-small"
                                        style={{ position: 'absolute', top: '-5px', right: '-5px' }}
                                        onClick={() => removeAttachment(index)}
                                        aria-label="Remove attachment"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Buttons column on the right */}
            <div className="is-flex is-flex-direction-row" style={{ justifyContent: 'flex-start' }}>
                {/* Attachment button */}
                <div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleAttachmentUpload}
                        style={{ display: 'none' }}
                    />
                    <button
                        type="button"
                        className={`button is-small is-static ${isUploading ? 'is-loading' : ''}`}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={true}
                        style={{
                            height: '40px',
                            width: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0',
                            borderRadius: '4px',
                            marginLeft: -50,
                            marginTop: 5,
                            border: 'none',
                            fontSize: 16,
                            opacity: 0.5,
                            cursor: 'not-allowed'
                        }}
                        title="File attachments temporarily disabled"
                    >
                        <span className="icon">
                            <i className="fas fa-paperclip"></i>
                        </span>
                    </button>
                </div>

                {/* Submit button */}
                <button
                    type="submit"
                    className="button is-primary"
                    style={{
                        height: '50px',
                        width: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0',
                        borderRadius: '4px',
                    }}
                    title="Send message"
                >
                    <span className="icon">
                        <Icon source={ArrowRightIcon as IconSource} />
                    </span>
                </button>
            </div>
        </form>
    )
}

export default React.memo(TextInput)
